import React from 'react';

import {useLocalesMgr, useRoute} from 'coreHooks';
import {Head} from 'coreUtils/adapters/head';
import {absoluteUrl} from 'coreUtils/url';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {IJsonLdProps} from '../ISeoProps';

/**
 * JSON of a JSON-LD script, with `<` as `\u003c` (same data for JSON parsers): CMS or catalog text containing
 * `</script>` cannot close the script element, also where the text is written as is (react-helmet-async).
 * @param {unknown} value - JSON-LD object
 * @returns {string} - Script text
 */
export const serializeJsonLd = (value: unknown): string => JSON.stringify(value).replace(/</g, '\\u003c');

const JsonLd = ({jsonLd}: IJsonLdProps) => {
	const {managerRepository} = useGeneralProvider();
	const schemaOrganizationConfig = managerRepository
		.getConfigurationsMgr()
		.get('sitePreferences.schemaOrganizationConfig');
	const hasJsonLd = jsonLd && jsonLd.length >= 0;
	const route = useRoute();
	const localesMgr = useLocalesMgr();

	if (!schemaOrganizationConfig && !hasJsonLd) {
		return null;
	}

	const processedJsonLd = [];
	const currentLocale = localesMgr.getCurrentLocale();
	const shouldShowSchemaOrganizationConfig = route.isHomePage && schemaOrganizationConfig;

	const processProductNode = (node) => {
		const offersSource = Array.isArray(node?.offers) ? node.offers[0] : node?.offers;

		return {
			...node,
			url: absoluteUrl(localesMgr.updateLinkWithLocale(node.url, currentLocale)),
			image: Array.isArray(node.image) ? node.image : absoluteUrl(node.image),
			'@id': node['@id']
				? absoluteUrl(localesMgr.updateLinkWithLocale(node['@id']))
				: undefined,
			mainEntityOfPage: node.mainEntityOfPage
				? absoluteUrl(localesMgr.updateLinkWithLocale(node.mainEntityOfPage))
				: undefined,
			...(offersSource && {
				offers: {
					...offersSource,
					url: offersSource.url
						? absoluteUrl(localesMgr.updateLinkWithLocale(offersSource.url, currentLocale))
						: undefined,
					...(offersSource.seller && {
						seller: {
							...offersSource.seller,
							'@id': offersSource.seller['@id']
								? absoluteUrl(offersSource.seller['@id'])
								: undefined,
						},
					}),
				},
			}),
			...(node.brand && {
				brand: {
					...node.brand,
					url: node.brand.url ? absoluteUrl(node.brand.url) : undefined,
					'@id': node.brand['@id'] ? absoluteUrl(node.brand['@id']) : undefined,
				},
			}),
		};
	};

	const processOrganizationNode = (node) => ({
		...node,
		url: node.url ? absoluteUrl(node.url) : undefined,
		'@id': node['@id'] ? absoluteUrl(node['@id']) : undefined,
		...(node.owner && {
			owner: {
				...node.owner,
				url: node.owner.url ? absoluteUrl(node.owner.url) : undefined,
				'@id': node.owner['@id'] ? absoluteUrl(node.owner['@id']) : undefined,
			},
		}),
	});

	if (hasJsonLd) {
		const parseItem = (item) => {
			if (item['@graph']) {
				processedJsonLd.push({
					...item,
					'@graph': item['@graph'].map((graphNode) => {
						if (graphNode['@type'] === 'Product') {
							return processProductNode(graphNode);
						}

						if (graphNode['@type'] === 'Organization') {
							return processOrganizationNode(graphNode);
						}

						return graphNode;
					}),
				});

				return;
			}

			const itemType = item['@type'];

			switch (itemType) {
				case 'WebSite':
					return null;

				case 'WebPage':
					processedJsonLd.push({
						...item,
						url: absoluteUrl(localesMgr.updateLinkWithLocale(item.url, currentLocale)),
						potentialAction: {
							...item.potentialAction,
							target: localesMgr.updateLinkWithLocale(
								item.potentialAction.target,
								currentLocale,
							),
						},
					});

					break;
				case 'BreadcrumbList':
					processedJsonLd.push({
						...item,
						itemListElement: item.itemListElement.map((item) => {
							let relativePath = localesMgr.updateLinkWithLocale(
								item.item,
								currentLocale,
							);
							let absolutePath = absoluteUrl(relativePath);

							return {
								...item,
								item: absolutePath,
							};
						}),
					});

					break;
				case 'ItemList':
					processedJsonLd.push({
						...item,
						itemListElement: item.itemListElement.map((item) => {
							if (item.url) {
								return {
									...item,
									url: absoluteUrl(
										localesMgr.updateLinkWithLocale(item.url, currentLocale),
									),
								};
							}
						}),
					});

					break;
				case 'Product':
					processedJsonLd.push(processProductNode(item));

					break;
				default:
					processedJsonLd.push(item);

					break;
			}
		};

		const parseJsonLdItemArray = (jsonLdItemArray) => {
			jsonLdItemArray.forEach((jsonLdItem) => {
				Array.isArray(jsonLdItem) ? parseJsonLdItemArray(jsonLdItem) : parseItem(jsonLdItem);
			});
		};

		parseJsonLdItemArray(jsonLd);
	}

	return (
		<Head>
			{shouldShowSchemaOrganizationConfig && (
				<script type="application/ld+json">{serializeJsonLd(schemaOrganizationConfig)}</script>
			)}

			{processedJsonLd.length > 0 &&
				processedJsonLd.map((jsonLdItem, index) => (
					<script key={`jsonld${index}`} type="application/ld+json">
						{serializeJsonLd(jsonLdItem)}
					</script>
				))}
		</Head>
	);
};

export default JsonLd;
